/**
 * LoginPage.js
 * Talkroute
 *
 * Created by Vel Genov on 12/8/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */

import { clearAuthError } from "@actions/userActions";
import { isDesktop } from "@constants/appConstants";
import ipcConstants from "@constants/ipcConstants";
import { domIds } from "@constants/testConstants";
import uiString from "@constants/uiString";
import useLocalStorage, { localStorageItems } from "@helpers/hooks/useLocalStorage.hook";
import TALKROUTE_LOGO from "@resources/icons/aboutScreenIcon.png";
import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useAuthContext } from "../context/AuthProvider";
import appConfig from "../config";
import { useAppSelector } from "../helpers/hooks/useAppSelector.hook";

const TALKROUTE_LOGO_ALT_TEXT = "Talkroute logo";

/**
 * The login page. Displayed to the user to take in their login credentials
 */
const LoginPage: React.FC = () => {
  const ipcRenderer = appConfig.electron?.ipcRenderer;
  const [userName, setUserName] = useState("")
  const [password, setPassword] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const authError = useAppSelector(state => state.authentication.authError)
  const dispatch = useDispatch();
  const { removeAllToasts } = useToasts();
  const [lastLoggedInUser, setLastLoggedInUser] = useLocalStorage(localStorageItems.lastLoggedInUser, "");
  const {login} = useAuthContext();
  const impersonateUserId = useAppSelector(state => state.impersonateUserReducer.impersonateUserId);
  /**
   * Called when the user selects login
   * Does basic validation and dispatches a login action
   *
   * @param {*} event reference to the caller
   */
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true)

    if (userName && password) {
      login(userName, password);
      isDesktop && setLastLoggedInUser(userName);
    } else {
      clearAuthError();
    }
  };

  /**
 * Called when the user name is typed
 *
 * @param {*} event reference to the caller
 */
  const changeUserName = (event: ChangeEvent<HTMLInputElement>) => {
    setUserName(event.target.value)
  }

  /**
 * Called when the user password is typed
 *
 * @param {*} event reference to the caller
 */
  const changePassword = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
  }

  /**
   * Display an error message
   *
   * @param {*} errorMessage the error message to display
   */
  const showError = (errorMessage: string | null) =>
    errorMessage ? (
      <div className="ui error message" style={{ maxWidth: "100%" }}>
        {errorMessage}
      </div>
    ) : null;

  /**
   * Default constructor. Sets up the props and function binding
   *
   * @param {*} props
   */
  useEffect(() => {
    ipcRenderer?.emit(ipcConstants.CLOSE_CONNECTION_LOST_NOTIFICATION);
    ipcRenderer?.emit(ipcConstants.CLOSE_RECONNECTING_NOTIFICATION)
    isDesktop && lastLoggedInUser && setUserName(lastLoggedInUser);
    return () => removeAllToasts()
  }, [])

  return (
    <div
      className="ui two column middle aligned center aligned grid"
      style={{ height: "100%" }}
    >
      <div className="column">
        <h2 className="ui image header" style={{ margin: "0" }}>
          <img
            src={TALKROUTE_LOGO}
            alt={TALKROUTE_LOGO_ALT_TEXT}
            className="image"
            style={{ width: "120px", margin: "0" }}
          />
        </h2>

        <form
          className="ui large form error"
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          {impersonateUserId && (
            <div className="ui message">
              You are going to impersonate a user with ID: {impersonateUserId}
            </div>
          )}
          <div
            className="ui segment"
            style={{ border: "none", boxShadow: "none", width: "350px" }}
          >
            <div
              className={`field  ${submitted && !userName && !authError ? " error" : ""
                }`}
            >
              <div className="ui input">
                <input
                  id={domIds.LOGIN_PAGE_EMAIL_INPUT}
                  type="text"
                  name="userName"
                  placeholder={uiString.authentication.EMAIL_ADDRESS}
                  value={userName}
                  onChange={changeUserName}
                  style={{ borderRadius: "8px" }}
                />
              </div>
            </div>
            {submitted && !userName && !authError && (
              <div className="field error">
                <div className="help-block">{`${uiString.authentication.EMAIL_ADDRESS} ${uiString.authentication.IS_REQUIRED}`}</div>
              </div>
            )}
            <div
              className={`field  ${submitted && !password && !authError ? " error" : ""
                }`}
            >
              <div className="ui input">
                <input
                  id={domIds.LOGIN_PAGE_PASSWORD_INPUT}
                  type="password"
                  name="password"
                  autoFocus={isDesktop && Boolean(lastLoggedInUser)}
                  placeholder={uiString.authentication.PASSWORD}
                  value={password}
                  onChange={changePassword}
                  style={{ borderRadius: "8px" }}
                />
              </div>
            </div>
            {submitted && !password && !authError && (
              <div className="field error">
                <div className="help-block">{`${uiString.authentication.PASSWORD} ${uiString.authentication.IS_REQUIRED}`}</div>
              </div>
            )}
            {showError(authError)}
            <button
              id={domIds.LOGIN_PAGE_LOGIN_BUTTON}
              className="ui fluid large orange submit button text-normal"
              type="submit"
              style={{
                fontWeight: "normal",
                borderRadius: "20px",
                backgroundColor: "var(--color-pumpkin-orange)",
              }}
            >
              {uiString.authentication.LOGIN}
            </button>
            <div
              style={{
                display: "flex",
                padding: "20px 15px",
                justifyContent: "space-between",
              }}
            >
              <a
                href={uiString.SIGN_UP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="text-normal"
              >
                {uiString.authentication.CREATE_ACCOUNT}
              </a>
              <a
                href={uiString.FORGOT_PWD_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="text-normal"
              >
                {uiString.authentication.FORGET_PASSWORD}
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LoginPage
